import BaseModule from '../_base_module';
import {
  apnsEnvironment,
  checkPermission,
  createNotificationChannels,
  currentPlatform,
  isPushSupported,
  requestPermission,
  resolveScreenRoute,
  revokeOsRegistration,
  startRegistration,
  stopRegistration,
} from '../../js/push';

/**
 * Push device registration (PUSH-01, PUSH-02, PUSH-04).
 *
 * The token is held here so logout can unregister exactly this device and leave
 * the member's other devices working.
 *
 * ============================================================================
 *  NOTE ON PERSISTENCE, AND THE TWO KINDS OF STATE IN THIS MODULE (B3/C5)
 * ============================================================================
 *
 * vuex-persist is configured with no reducer (src/store/index.js), so it mirrors
 * the whole store -- including this module -- into localStorage and rehydrates it
 * on every cold launch. That is not a detail: it means EVERY field below comes
 * back after a restart whether or not it still means anything.
 *
 * So each field is annotated with one of exactly two kinds, and there is no third:
 *
 *   PER-PROCESS       meaningless the moment the process ends. A restored value is
 *                     a LIE about the process now running, and must be reset at
 *                     boot -- see the `reset_session_state` mutation.
 *   SERVER/DEVICE FACT meaningful across restarts, because it describes something
 *                     outside this process that the client can still rely on: the
 *                     platform token it holds, or a property of the phone.
 *
 * Only FOUR fields are the second kind: `token`, `permission_prompt_shown`,
 * `pending_unregistration` and its legacy sibling `pending_unregistration_token`.
 * Everything else here, including the registration claim, is per-process. A CLAIM ABOUT A SERVER ROW IS NOT A SERVER FACT: the
 * client cannot verify it, and the server can invalidate it silently (its reaper
 * deletes rows on delivery failure, and finding C3 is that it can do so for a
 * token that is still alive). A claim that outlives the process that made it is
 * therefore indistinguishable from a stale one, so it is not kept.
 *
 * THE TOMBSTONE IS NOT A COUNTER-EXAMPLE TO THAT RULE, and it is worth being
 * precise about why, because it is the one field added after the rule was written
 * and it looks like an exception. `registered` is a claim about what the SERVER
 * holds -- unverifiable, silently falsifiable, therefore per-process.
 * `pending_unregistration` is a record of what THIS CLIENT FAILED TO DO.
 * The client is the only witness to that fact, nothing outside can invalidate it,
 * and its entire purpose is to outlive the process that failed. Persisting it is
 * the requirement, not a concession.
 *
 * WHAT WENT WRONG WHEN THIS DISTINCTION DID NOT EXIST (finding B3 = C5).
 * `registered` is a claim about the SERVER, and phase 39's ignition used it as a
 * PER-PROCESS guard: `src/js/push-session.js` returned early when it was true,
 * before `push/register`. But `register()` is what creates the Android channels
 * AND attaches all four native listeners. So a handset that restored
 * `registered: true` attached NO listeners at all -- FCM/APNs could rotate its
 * token and nothing was listening to hear it, a tapped notification was never
 * routed, and channels were never reconciled. Push stayed silently dead until the
 * member logged out. Two independent reviewers found it; it is one defect.
 *
 * WHY THERE IS NO `reducer` ON VuexPersistence, AND WHY ONE MUST NOT BE ADDED.
 * A reducer is the obvious-looking fix and it is the wrong one, because it applies
 * to the WHOLE STORE, not to this module. It would have to re-enumerate every
 * module that currently depends on persistence -- `current_user`, and
 * `permission_prompt_shown` below, which is deliberately designed to survive both
 * a restart and a logout (PREF-05) -- and the first module anybody adds afterwards
 * would silently stop persisting, with no test to notice. The plugin therefore
 * stays unreduced and the per-process fields are reset at boot instead, which is a
 * change local to this file. If the PREF-05 explainer ever starts re-appearing
 * after a restart, a reducer is the first thing to look for.
 *
 * The token is a device identifier tied to a member, so it is never logged.
 */

/**
 * How long logout will wait for a registration POST that is already in flight
 * (finding F3), in milliseconds.
 *
 * WHY THERE IS A BOUND AT ALL. `callApi` passes axios NO `timeout`
 * (src/store/modules/api.js), so a request that never answers never settles -- and an
 * unbounded await here would turn a hung POST into a logout that never completes.
 * That is a WORSE defect than the race it closes: the race costs a logged-out handset
 * a delivery route it should not have, the hang costs the member the ability to log
 * out at all, in the one situation where they most want to (a phone about to change
 * hands, on a bad connection).
 *
 * WHY THREE SECONDS. The thing being waited for is a single small POST that has
 * ALREADY BEEN SENT, so this is a settle time and not a round-trip budget. Three
 * seconds is well beyond a mobile round trip and comfortably short of the point where
 * a member decides the button did not work and starts pressing things. There is
 * nothing magic about the number and it is asserted in the tests to within 100ms, so
 * changing it is a deliberate act rather than a silent one.
 *
 * THE BOUND OPENS A HOLE AND THE HOLE IS NOT LEFT SILENT: when the wait expires, the
 * POST may still land after the DELETE, so `unregister` records a tombstone under the
 * departing member exactly as a failed DELETE does. The next login pays it off.
 */
const REGISTRATION_SETTLE_TIMEOUT_MS = 3000;

/**
 * Wait for `promise` to settle -- either way -- but never longer than `ms`.
 *
 * SETTLED, NOT FULFILLED. A registration that failed is finished, and finished is all
 * this caller needs: a rejection cannot recreate the server row. Letting a rejection
 * propagate would make a failed device registration able to break a logout, which is
 * the rule the whole C1 tombstone exists to keep.
 *
 * HONEST NOTE ABOUT THAT REJECTION HANDLER: it is BELT AND BRACES and no test can kill
 * it on its own. The only promise handed to this function today is
 * `pending_registration_request`, which `register()` already stores in an
 * already-handled form, so it cannot reject in the first place. Removing either guard
 * alone changes nothing; the outer one is the one under test (`records a failed
 * registration as a SETTLED request`). It is kept because this signature invites being
 * handed a raw promise later, and the cost of that going wrong is a logout that
 * throws.
 *
 * The timer is cleared on both paths so a logout cannot leave a pending timeout
 * behind it.
 *
 * @returns {Promise<boolean>} true if it settled within the bound, false if the bound
 *   expired first.
 */
function settleWithin(promise, ms) {
  let timer = null;

  const bounded = new Promise((resolve) => {
    timer = setTimeout(() => resolve(false), ms);
  });

  return Promise.race([
    Promise.resolve(promise).then(() => true, () => true),
    bounded,
  ]).then((settled) => {
    clearTimeout(timer);

    return settled;
  });
}

/**
 * Do these two identifiers name the same member?
 *
 * FINDING F2 IS ENTIRELY THIS QUESTION. A tombstone may only be spent by the member
 * who created it, because DELETE /push-tokens is scoped to the authenticated
 * principal server-side and answers 204 even when it matched nothing -- so a DELETE
 * sent by the wrong member proves nothing and clears everything.
 *
 * Compared as STRINGS because the id reaches this function from two places: the live
 * store, where it is whatever the API sent, and localStorage, where it has been
 * through JSON. A `5` that came back as `'5'` is the same person.
 *
 * NULL IS NEVER A MATCH, NOT EVEN AGAINST ANOTHER NULL, and that is the migration
 * decision for plan 39.5-05's bare-string tombstone. A hydrated legacy value has no
 * recorded owner, and "unknown" cannot be shown to be "this member" -- treating it as
 * a match would restore the defect for exactly the handsets that already have one.
 * Unknown is read as somebody else's, which routes it to the OS-level revocation
 * below: the half that needs no server authority and is safe under any principal.
 */
function isSameMember(a, b) {
  if (a === null || a === undefined || b === null || b === undefined) {
    return false;
  }

  return String(a) === String(b);
}

/**
 * Discharge the outstanding revocation, in BOTH the current field and the legacy one.
 *
 * Always both. The legacy field is only ever non-null on a handset upgrading from
 * plan 39.5-05's shape, and leaving it set would re-run the migration on every launch
 * for the rest of the installation's life.
 */
function clearPendingUnregistration(commit) {
  commit('set_state', { property: 'pending_unregistration', value: null });
  commit('set_state', { property: 'pending_unregistration_token', value: null });
}

/**
 * State as a factory, not a shared object.
 *
 * The other modules in this store export a plain object, which means every Vuex
 * store created in the same process shares one state instance -- fine in the
 * app, where there is exactly one store, but it leaks between tests. This
 * module opts into the isolated form so its tests do not depend on ordering.
 */
const state = () => ({
  ...BaseModule.state,

  // SERVER/DEVICE FACT. The platform token for THIS device, once registration has
  // completed. Persisted on purpose: logout's DELETE /push-tokens needs it, and
  // without it a logged-out handset keeps receiving the member's notifications.
  token: null,

  // PER-PROCESS. 'granted' | 'denied' | 'prompt' | null (never asked / not
  // applicable). An OS fact, but a re-readable one, and a stale copy is worse than
  // none: the member can revoke notifications in system settings while the app is
  // closed. `refresh_permission` re-reads it on every ignition, and both screens
  // that render off it (notificaties.vue, calendar.vue) dispatch that first.
  permission: null,

  // PER-PROCESS. A CLAIM, not a fact: "a POST for `registered_token` was accepted
  // by the API during THIS process".
  //
  // WHY PER-PROCESS RATHER THAN PERSISTED, WHICH IS THE SUBTLE HALF OF B3/C5.
  // Persisting it looks right -- a server row does outlive the app, so surely a
  // record of it should too. But the client cannot verify the claim, and there is a
  // path that falsifies it WITHOUT the token changing: the server's
  // ReapInvalidPushTokens listener DELETES a row on a delivery failure, and finding
  // C3 is that it can do so for a token that is perfectly alive (a
  // `BadDeviceToken` caused by a wrong APNs environment is read as permanent). The
  // handset is told nothing. So on the next launch the token is unchanged, a
  // persisted claim would still say "the server knows it", the POST would be
  // skipped as redundant -- and the row would stay deleted. The handset would be
  // silently unreachable, which is the exact harm this finding is about, merely
  // moved one step further along.
  //
  // Resetting it at boot costs exactly ONE request per launch (the server's
  // registration is `firstOrNew` on the token, so it is idempotent) and buys
  // self-healing for both falsifying paths: a rotated token AND a reaped row.
  // That is the trade the security triage asks for in as many words: "If FCM
  // rotates a token OR THE REAPER DELETES THE ROW, the handset restores
  // registered=true, skips registration on every launch, and never sends the
  // replacement."
  //
  // It says NOTHING about whether this process has attached any listeners -- read
  // `session_registered` for that. Conflating the two is the other half of B3/C5.
  registered: false,

  // PER-PROCESS. The exact token the claim above was recorded for.
  //
  // This field is what makes the skip decision expressible at all. A bare boolean
  // can only say "some token was accepted", never "THIS token was" -- so with a
  // boolean alone there is no way to tell a redundant POST (the same token, already
  // sent moments ago by the other of app.vue's two call sites) from a required one
  // (the platform handed us a different token). Compared byte for byte at the skip
  // site; see `needs_token_post`.
  registered_token: null,

  // PER-PROCESS. Has THIS process completed its push ignition -- i.e. has
  // `register()` run to the point where the four native listeners are attached and
  // the Android channels are reconciled?
  //
  // Reset to false at boot by `reset_session_state`, because a restored `true` is
  // a statement about a process that no longer exists. It is deliberately NOT used
  // as a guard in front of `register()`: that is the shape of the defect this
  // module was fixed for, and re-adding it would reintroduce it the moment the
  // boot reset is missed.
  session_registered: false,

  // PER-PROCESS. Last native registration failure, for diagnosis. Never shown to
  // the member, and never carried over from a previous launch -- an error from a
  // process that has since been killed is not a diagnosis of this one.
  registration_error: null,

  // PER-PROCESS. Set when a notification tap arrives; the app component consumes
  // and clears it. Held in the store rather than acted on directly because a
  // cold-start tap can fire before the router exists.
  pending_navigation: null,

  // PER-PROCESS. The target a tapped notification named, parked for the landing
  // screen and for nothing else:
  //
  //     { screen: string, id: number|string|null }  |  null
  //
  // WHY IT EXISTS AT ALL. `consume_pending_navigation` returns a bare path string
  // and clears the whole pending object, and app.vue's watcher consumes it BEFORE
  // router.navigate mounts the page -- so a landing page cannot read the id from
  // `pending_navigation`. Measured (41-RESEARCH Finding D), not inferred. Parking
  // it here is the only way the page that the tap opened can know WHICH booking
  // the tap was about.
  //
  // WHAT IT IS NOT. It is scroll-into-view and `source: 'push'` EVIDENCE. It never
  // steers the path (that comes from resolveScreenRoute's allowlist, from
  // `pending.screen`, and from nothing else) and it never authorises anything: the
  // API decides whose confirmation it is from the authenticated principal, and
  // `source` is a column nothing branches on.
  //
  // Spent by the first read (`consume_last_tapped_target`) and cleared by
  // `reset_session_state`, per UI-SPEC IC5's two clauses.
  last_tapped_target: null,

  // SERVER/DEVICE FACT. Finding C1's second half, as finding F2 corrected it. The
  // outstanding DELETE /push-tokens this device still owes, or null when it owes
  // none:
  //
  //     { token: string, owner_user_id: number|string|null }
  //
  // IT CARRIES ITS OWNER, AND THAT IS THE WHOLE OF FINDING F2. Plan 39.5-05 stored
  // the token alone. On the next login the client sent an authenticated DELETE as
  // whoever was logged in THEN, and `PushTokenController::destroy()` scopes its
  // delete to `$request->user()->pushTokens()` and answers 204 even when it removed
  // nothing -- deliberately, so a logout cannot fail because the row was already
  // gone. So a handset handed from member A to member B did this: B's login sent A's
  // DELETE as B, matched no row, got its 204, and cleared A's tombstone permanently
  // -- while A's registration stayed live and kept delivering A's private
  // notifications to B's phone. Plan 39.5-04's 409 then stopped B claiming the
  // device, so the situation could not correct itself either.
  //
  // A MISMATCH IS NOT DISCARDED, IT IS REROUTED. The retry falls back to the OS-level
  // `unregister()` (see `retry_pending_unregistration`), which is the half of the
  // revocation that needs no server authority and can therefore be performed by
  // whoever is holding the device. The tombstone survives if that fails too.
  //
  // WHAT IT MUST NOT BECOME: a DELETE that is global by token. That would let any
  // principal delete any device row and reopens findings C2 and C4, which plans 04
  // and 05 closed on purpose. The device-scoped installation credential that would
  // authorise cross-owner revocation properly is deferred to a later release --
  // .planning/todos/pending/2026-08-17-lock-an-account-to-a-single-device.md
  //
  // WHY IT EXISTS. `unregister` below swallows a failed DELETE, and it must: a
  // member has to be able to log out with no connectivity, and a logout that
  // failed because a device registry could not be reached would be worse than
  // useless. But swallowing it left the row in place with nothing on either side
  // remembering to try again -- the server kept a live route to a handset the
  // member had walked away from, and its reaper could not help, because delivery
  // to that handset SUCCEEDS. This field is what turns "swallowed" into
  // "deferred".
  //
  // WHY IT IS PERSISTED, WHICH IS THE OPPOSITE CLASSIFICATION FROM `registered`.
  // See the module docblock. `registered` is an unverifiable claim about the
  // server; this is a record of what this client failed to do, and the process
  // that failed is precisely the one that must not be trusted to remember it. A
  // per-process tombstone would be cleared by the very restart it exists to
  // survive.
  //
  // IT HOLDS THE TOKEN, NOT A BOOLEAN, for the same reason `registered_token`
  // does: the local `token` is cleared during logout, so by the time anything
  // could act on a flag the value needed to act is gone.
  //
  // ACCEPTED, AND ALREADY ACCEPTED FOR `token` ABOVE: this puts a device
  // identifier in localStorage for longer than the session that produced it. It
  // is the same value that field already holds, it is required by the DELETE it
  // exists to retry, and it is cleared the moment that DELETE lands. Finding F2
  // adds the member id beside it, which is data the store already persists in
  // `current_user` and does not widen what a stolen localStorage blob reveals.
  pending_unregistration: null,

  // SERVER/DEVICE FACT, LEGACY. Plan 39.5-05's tombstone: a bare token string with
  // no owner. NEVER WRITTEN ANY MORE -- it exists so that a handset which persisted
  // one under the old shape is not stranded with an outstanding revocation nothing
  // will ever look at again.
  //
  // Read in exactly one place, the `pending_unregistration` getter, which normalises
  // it to `{ token, owner_user_id: null }`. An unknown owner is read as somebody
  // else's (see `isSameMember`), so a legacy tombstone takes the OS-revocation path
  // and is never sent to the server under a principal that cannot honour it. That is
  // the safe reading, and it is the only one that does not re-open F2 on precisely
  // the handsets that already carry the defect.
  //
  // It is cleared alongside the new field whenever the revocation is discharged, so
  // the migration runs at most once per outstanding tombstone.
  pending_unregistration_token: null,

  // PER-PROCESS. Finding F3. The registration POST that is currently in flight, or
  // null. Holds a PROMISE, which is unusual for Vuex state and is deliberate.
  //
  // WHY IT EXISTS. `register()`'s token listener used to `dispatch('send_token_to_api',
  // token)` without returning, awaiting or tracking the result. Logout removed the
  // listeners and sent its DELETE but waited for nothing, so a POST that had captured
  // the member's bearer token before the logout could land AFTER the DELETE and
  // recreate the row -- on a handset the member had just logged out of, with no
  // tombstone kept, because from the client's point of view the row was gone.
  //
  // WHY A PROMISE IN STATE IS SAFE HERE. Vue 3's `reactive()` returns non-plain
  // objects (Promise among them) unproxied, so what comes back out is the promise
  // itself and not a proxy whose `then` would throw on an incompatible receiver.
  // vuex-persist stringifies it to `{}`, which is meaningless and harmless because
  // this field is PER-PROCESS and the boot reset clears it before anything reads it.
  //
  // WHY IT IS PER-PROCESS AND NOT A SERVER/DEVICE FACT: a request from a process that
  // no longer exists is not in flight. There is nothing to wait for.
  pending_registration_request: null,

  // SERVER/DEVICE FACT (the device half). PREF-05. Has this device already been
  // shown the in-app explainer that precedes the OS permission prompt?
  //
  // Three things about this flag are deliberate:
  //
  //  1. It is DEVICE state, not account state. The question being asked is "has
  //     this device been asked yet", which is a property of the phone and of the
  //     OS permission attached to it -- not of whoever happens to be logged in.
  //     Storing it per account would re-prompt on every account switch for an OS
  //     permission that is already settled.
  //  2. It persists for free. vuex-persist runs with NO reducer
  //     (src/store/index.js), so the whole store -- this module included -- is
  //     mirrored into localStorage under `private-gym`. No extra plumbing.
  //  3. It survives a logout, on purpose. `current_user/logout` clears
  //     current_user, activities, training_sessions, training_slots, news and
  //     notification_preferences. It deliberately does NOT clear `push`, so this
  //     flag outlives the session, which is exactly what "has this device been
  //     asked yet" means. The accepted cost is that a second member on a shared
  //     phone is not re-prompted; they should not be, because the OS permission
  //     they would be prompted for is already granted or already denied at the
  //     device level and re-asking cannot change it.
  //
  // Why it exists at all: iOS's permission prompt is ONE-SHOT. A member who
  // declines a cold prompt can never be asked again from inside the app. The
  // explainer protects that one shot, and this flag is what stops the explainer
  // itself from becoming the nag.
  permission_prompt_shown: false,
});

const getters = {
  ...BaseModule.getters,
  token(state) {
    return state.token;
  },

  /**
   * THE SERVER-STATE CLAIM. "A POST for `registered_token` was accepted, in this
   * process."
   *
   * Not "this device is reachable" -- the client cannot know that -- and above all
   * not "this process has ignited". Anything asking whether the listeners are
   * attached wants `is_session_registered`; using this getter for that question is
   * findings B3/C5 verbatim, and the reason it is no longer read anywhere in `src/`.
   */
  is_registered(state) {
    return state.registered;
  },

  /** The token the claim above was recorded for, or null if there is no claim. */
  registered_token(state) {
    return state.registered_token;
  },

  /**
   * THE PER-PROCESS FACT. "This process has attached its listeners and reconciled
   * its channels."
   *
   * The counterpart to `is_registered`, and the reason the two are separate
   * getters rather than one field doing both jobs badly. False on every fresh boot,
   * whatever localStorage says.
   */
  is_session_registered(state) {
    return state.session_registered;
  },

  /**
   * Would POSTing `token` tell the server anything it does not already know?
   *
   * The one place the claim and the token it refers to are compared, expressed as a
   * getter so the condition has a name, one definition, and a test.
   *
   * THREE CASES MEAN POST, and only the fourth means skip:
   *   - no claim at all              -> POST. Covers every cold launch (the claim is
   *                                     per-process and reset at boot), the first
   *                                     registration, and the first one after a
   *                                     logout. This is what makes a REAPED SERVER
   *                                     ROW heal itself: the token did not change,
   *                                     so nothing else here could notice.
   *   - a claim for a DIFFERENT token -> POST. The platform rotated it. This is the
   *                                     case a bare boolean could not see at all.
   *   - a claim for no token          -> POST. Defensive: a claim without a subject
   *                                     is not something to trust.
   *   - a claim for THIS EXACT token  -> skip. app.vue has two call sites that can
   *                                     both fire within one launch, and re-POSTing
   *                                     between them would rewrite a row with its
   *                                     own contents for no reason.
   */
  needs_token_post(state) {
    return (token) => !state.registered || state.registered_token !== token;
  },

  /**
   * THE OUTSTANDING REVOCATION, normalised across both shapes it can be stored in.
   *
   * Returns `{ token, owner_user_id }` or null. THE ONE PLACE the legacy bare-string
   * tombstone from plan 39.5-05 is read, and the one place the migration decision
   * lives: a hydrated legacy value has no recorded owner, so it is normalised to
   * `owner_user_id: null` -- which `isSameMember` never matches, so it can never be
   * sent to the server under a principal that could not have created it.
   *
   * A getter rather than a raw state read at the call sites, so the concept has one
   * name, one definition, one test, and the two callers cannot drift.
   */
  pending_unregistration(state) {
    if (state.pending_unregistration) {
      return state.pending_unregistration;
    }

    if (state.pending_unregistration_token) {
      return { token: state.pending_unregistration_token, owner_user_id: null };
    }

    return null;
  },

  /**
   * The token this device still owes the server a DELETE for, or null.
   *
   * Kept as its own getter because "which token" and "whose" are separate questions
   * and a caller asking the first should not have to know the record's shape.
   */
  pending_unregistration_token(state, getters) {
    return getters.pending_unregistration ? getters.pending_unregistration.token : null;
  },

  permission(state) {
    return state.permission;
  },
  // Phase 39 uses this to show a "notifications are off in your phone settings"
  // banner without flipping the member's own category toggles.
  is_blocked_at_os_level(state) {
    return state.permission === 'denied';
  },

  has_shown_permission_prompt(state) {
    return state.permission_prompt_shown;
  },

  /**
   * The whole PREF-05 precondition, in one place (Plan 39-11's trigger reads this).
   *
   * Three conditions, and they are here rather than inlined at the call site
   * precisely because three inlined conditions drift: a later caller that
   * remembers two of them re-prompts a member who already declined, or fires an
   * explainer on the web where there is no OS prompt behind it to accept.
   *
   * `permission === 'prompt'` and not `!== 'granted'` on purpose. 'denied' means
   * the member already said no at OS level; on iOS the prompt is spent and the
   * explainer would lead to a button that does nothing. The settings screen's
   * PREF-04 banner is the path for that member, not this one.
   */
  should_offer_permission_prompt(state, getters) {
    return isPushSupported()
      && state.permission === 'prompt'
      && !getters.has_shown_permission_prompt;
  },
};

const actions = {
  ...BaseModule.actions,

  /**
   * Ask the OS for permission, then register if granted.
   *
   * Call in context (Phase 39: after the first booking). Never on app launch.
   */
  async request_permission_and_register({ commit, dispatch }) {
    const permission = await requestPermission();

    commit('set_state', { property: 'permission', value: permission });

    if (permission !== 'granted') {
      return false;
    }

    return dispatch('register');
  },

  /**
   * Refresh the cached OS permission state.
   *
   * Worth doing on resume: the member may have turned notifications off in
   * system settings while the app was backgrounded.
   */
  async refresh_permission({ commit }) {
    const permission = await checkPermission();

    commit('set_state', { property: 'permission', value: permission });

    return permission;
  },

  /**
   * Start native registration. The token arrives asynchronously on the
   * 'registration' event, so this resolves before the token exists.
   *
   * THE ORDER IS THE POINT: fetch preferences -> create channels -> register().
   *
   * An Android channel's importance is immutable once the channel exists on a
   * device, and a channel created lazily on first use does not exist at the
   * moment the notification carrying its `channel_id` lands -- Android falls back
   * to FCM's auto-created "Miscellaneous" channel at DEFAULT importance instead.
   * So the channels are built before anything that could carry a `channel_id`
   * can arrive, and they are built from the server's taxonomy, which means the
   * fetch has to come first. `Channel.name` is required by the plugin and the
   * names are server-owned, so there is no way to reorder these three.
   *
   * The fetch belongs here and not in src/js/push.js: that file has no store
   * access, which is why createNotificationChannels() takes the taxonomy as an
   * argument rather than going and getting it.
   */
  async register({ state, commit, dispatch }) {
    if (!isPushSupported()) {
      return false;
    }

    let categories = null;

    try {
      // Resolves to the five-entry array, or to null on failure. Documented never
      // to reject -- the try/catch is here anyway because the cost of that
      // contract quietly changing is the device never getting a token at all,
      // and a settings fetch is not worth that.
      categories = await dispatch('notification_preferences/fetch_all', null, { root: true });
    } catch (e) {
      categories = null;
    }

    if (categories) {
      await createNotificationChannels(categories);
    }
    // ELSE: no taxonomy, so no channels this run -- and registration continues
    // anyway. The trade is deliberate and it is not close. The token is what
    // makes the device reachable at all; without it nothing arrives, ever, until
    // the next launch. With it but without channels, a notification arriving in
    // the gap lands in FCM's "Miscellaneous" channel at DEFAULT importance, which
    // is wrong but RECOVERABLE: the channels are created on the next
    // registration, and every later notification uses the real one. Abandoning
    // registration because a settings endpoint was down would trade a recoverable
    // problem for a total one.
    //
    // No "channels created" flag guards any of this, on purpose. Recreating a
    // channel with identical values is a no-op on Android, so running every time
    // is free and it is what makes a deleted channel or a failed first attempt
    // heal itself. A flag would turn exactly this transient failure into a
    // permanent one. There is a test that fails the moment somebody adds one.

    const started = await startRegistration({
      onToken: (token) => {
        commit('set_state', { property: 'token', value: token });

        // TRACKED, NOT FIRE-AND-FORGET (finding F3). The plugin discards whatever
        // this callback returns, so there is nowhere to return the promise TO -- it
        // has to be put somewhere `unregister` can find it, or logout has no way to
        // know a POST carrying the member's bearer token is already on its way to
        // recreating the row it is about to delete.
        //
        // Stored already-handled (`.then(ok, fail)`), for two reasons. It gives
        // `unregister` a promise it can await without a rejection escaping into the
        // logout path, and it fixes an unhandled rejection that existed here
        // independently: `send_token_to_api` rethrows anything that is not a 403 or a
        // 409, and nothing was catching it.
        const request = dispatch('send_token_to_api', token).then(
          () => true,
          () => false,
        );

        commit('set_state', { property: 'pending_registration_request', value: request });

        // Cleared when it settles, but only if it is still the one being tracked --
        // a later registration in the same process must not have its request erased
        // by an earlier one finishing.
        request.then(() => {
          if (state.pending_registration_request === request) {
            commit('set_state', { property: 'pending_registration_request', value: null });
          }
        });
      },
      onError: (error) => {
        // Registration failing is not fatal to the app -- the member simply
        // does not get notifications. Record it for diagnosis, but never show
        // the member a dialog about a feature they may not have asked for.
        commit('set_state', { property: 'registration_error', value: String(error?.error || error) });
      },
      onTapped: (data) => {
        dispatch('handle_notification_tap', data);
      },
    });

    // PER-PROCESS. Recorded only after startRegistration() resolves, because that
    // is the call that attaches the four listeners -- so this says "this process is
    // wired up", which is the only thing it is allowed to say. Note what is NOT
    // recorded here: nothing about the server. The token has not even arrived yet
    // (it comes back later on the 'registration' event), let alone been accepted.
    commit('set_state', { property: 'session_registered', value: started === true });

    return started;
  },

  /**
   * Tell the API about this device (PUSH-01, PUSH-02).
   *
   * PUSH-06, client half. While an admin is impersonating, the API refuses this
   * with a 403 -- correctly, since the device belongs to the admin. But the
   * api module's global error handler turns any 4xx into a danger notification
   * (src/store/modules/api.js), so letting the call go out would flash
   * admin-tooling text in the member-facing UI for a refusal that is entirely
   * expected. So: don't make a request we know will be refused.
   *
   * The server-side check remains the actual control; this is UX, exactly like
   * the route guards in src/js/guards.js. The 403 catch below stays as the
   * backstop for the case where the client's view of impersonation is wrong.
   */
  send_token_to_api({ commit, dispatch, getters, rootState }, token) {
    if (rootState.current_user && rootState.current_user.origin_user) {
      return Promise.resolve(false);
    }

    // THE ONE PIECE OF WORK A PREVIOUS REGISTRATION IS ALLOWED TO SKIP (B3/C5).
    //
    // This is where phase 39's short-circuit belongs, and it is the only place it
    // could ever have been correct. It used to live in
    // src/js/push-session.js, in front of `push/register` -- so a restored
    // `registered: true` skipped the channel reconciliation and all four listener
    // attachments as well, and a handset whose token had rotated could no longer
    // even observe the replacement. See the long comment at that call site.
    //
    // Here the decision has the one input it needs: the token in hand. Skipping is
    // correct only when the server was already told about THIS EXACT token
    // (`needs_token_post` compares it against `registered_token`, byte for byte).
    // A rotated token, a claim recorded for a different token, or no claim at all
    // all mean send -- which is precisely what a bare boolean could not express and
    // why the guard could not be made correct where it used to sit.
    if (!getters.needs_token_post(token)) {
      return Promise.resolve(false);
    }

    const platform = currentPlatform();

    return dispatch(
      'api/postEndpoint',
      {
        endpoint: '/push-tokens',
        data: {
          token,
          platform,
          apns_environment: apnsEnvironment(),
          app_version: import.meta.env.VITE_APP_VERSION || null,
        },

        // NO GLOBAL DANGER TOAST FOR THIS CALL, and it is deliberate rather than
        // a widening of the flag api.js warns about.
        //
        // WITHOUT IT THE 409 BRANCH BELOW ACHIEVES NOTHING VISIBLE. The toast is
        // raised by `onError` from inside `callApi`, BEFORE this promise's catch
        // runs, and 409 has its own branch there with `hide_after: 60000`
        // (src/store/modules/api.js:229) -- a full minute of red bar carrying the
        // server's English message. Swallowing the rejection here would leave the
        // member staring at it. Measured: the no-toast test is red at one toast
        // against a fix that only extends the catch.
        //
        // WHY IT COVERS EVERY STATUS AND NOT JUST 409. The flag is read when the
        // request is dispatched, so it cannot be made conditional on a status
        // nobody knows yet -- and there is nothing to lose by that. This is a
        // BACKGROUND call: it is made by the app launching, never by a member
        // doing something, so there is no action of theirs that can be reported
        // as having failed and nothing they could do about any outcome. api.js's
        // own precedent is `news/fetch_all`, a supplementary panel that must not
        // put a red bar over a working page; a device registration the member has
        // never heard of is further from their intent than that.
        //
        // WHAT IS NOT SUPPRESSED, because `onError` keeps running: the error bag,
        // and the 401 branch's `current_user/logout`. Opting out of a message is
        // not opting out of being logged out. The promise still rejects, so
        // `does not swallow other errors` still holds.
        suppress_error_toast: true,
      },
      { root: true },
    )
      .then(() => {
        commit('set_state', { property: 'registered', value: true });
        // The claim and its subject are written together, always. A `registered`
        // without the token it refers to is the boolean-only state this fix
        // removed, and it cannot distinguish a redundant POST from a required one.
        commit('set_state', { property: 'registered_token', value: token });
        return true;
      })
      .catch((error) => {
        // callApi rejects with `error.response` unwrapped (see api.js), so the
        // status is on the top level here -- not under .response. Both shapes
        // are checked so this keeps working if that ever changes.
        const status = error?.status ?? error?.response?.status;

        if (status === 403) {
          // Impersonation. Not an error worth showing anyone.
          return false;
        }

        if (status === 409) {
          // FINDING C2, client half. The API refuses to move a push-token row
          // between members: a token that already belongs to somebody else is
          // answered with 409 and NOTHING IS WRITTEN
          // (PushTokenController::store(), plan 39.5-04). Before that change, any
          // member who could produce another member's token string seized that
          // member's push channel.
          //
          // A 409 IS A SUCCESSFUL OUTCOME OF A WELL-FORMED REQUEST. The server
          // understood it and declined on purpose. That makes it categorically
          // unlike an error, and it must not be presented as one -- see the
          // suppression on the request above, which is the half that actually
          // stops the member seeing anything.
          //
          // THE SERVER-STATE CLAIM STAYS FALSE. It is only ever set in the
          // `.then` above, and returning here rather than falling through keeps
          // it that way: the server explicitly did not accept this device, so the
          // app must not believe it is registered. The consequence is that the
          // next launch tries once more -- once per launch, not a retry loop --
          // which is correct, because the situation CAN change (the previous
          // owner's row can be removed) and the client is not told when it does.
          //
          // WHAT IT MEANS IN PRACTICE, AND THE MANUAL PATH. This handset is
          // registered to a different account. Fail-closed is the intended
          // behaviour (locked decision D-02: the cheap 409, not an installation
          // secret), and the accepted cost is that a GENUINE second-hand-device
          // handover now needs a human: delete the previous owner's push_tokens
          // row, and this handset registers itself on its next launch. The proper
          // fix -- an app-generated installation secret in Keychain/Keystore that
          // an installation can prove -- is deliberately not built here and moves
          // to a later release with the "one account, one device" feature:
          // .planning/todos/pending/2026-08-17-lock-an-account-to-a-single-device.md
          //
          // DELIBERATELY A SEPARATE BRANCH FROM THE 403 ABOVE, not `403 || 409`.
          // The two are different situations with different causes and different
          // follow-ups: the 403 is the impersonation refusal, for a call this
          // client already declines to make (see the `origin_user` guard), and is
          // a backstop for a client whose view of impersonation is wrong. The 409
          // arrives from a call that was correctly made by a member doing nothing
          // unusual. Merging them would delete the only place the second one is
          // explained. There is a structural test.
          return false;
        }

        throw error;
      });
  },

  /**
   * Unregister this device (PUSH-04, finding C1).
   *
   * Must run BEFORE the access token is cleared, or the request is
   * unauthenticated and the row survives -- which would leave the member's
   * notifications arriving on a device they logged out of.
   *
   * TWO INDEPENDENT REVOCATIONS, and the point of C1 is that only one of them
   * used to happen:
   *
   *   1. THE OS. `stopRegistration()` now calls `PushNotifications.unregister()`,
   *      which is what actually stops APNs and FCM delivering. It needs no
   *      network of ours and therefore works when everything else here does not.
   *   2. THE SERVER. The DELETE below removes the row, so nothing is sent in the
   *      first place. It needs connectivity, and when it does not get it the
   *      tombstone carries the work forward to the next login.
   *
   * Either one alone makes the handset stop receiving the member's notifications.
   * Before this plan the first did not exist and the second was discarded on
   * failure, so a logout with no connectivity revoked NOTHING -- which is the
   * finding, in one sentence.
   */
  async unregister({ state, commit, dispatch, getters, rootGetters }) {
    // WHO is logging out. Read HERE and not later: `current_user/logout` awaits this
    // action and only then commits `clear_all`, so this is the last moment the
    // principal exists -- and finding F2 is precisely that a tombstone without one is
    // spendable by the next member to hold the handset.
    const owner = rootGetters['current_user/id'] ?? null;

    // 1. STOP ACCEPTING NEW REGISTRATION CALLBACKS. `stopRegistration()` removes
    //    every native listener first, so no token delivered from here on can start
    //    another POST. Then it revokes at the OS.
    await stopRegistration();

    // 2. WAIT FOR THE ONE ALREADY IN FLIGHT (finding F3). Removing the listeners does
    //    nothing about a POST that has already left, and that POST carries a bearer
    //    token minted before the logout, so the API will honour it. Issuing the
    //    DELETE without waiting lets it arrive FIRST, succeed, and be overtaken by the
    //    POST -- which recreates the row on a handset the member has just logged out
    //    of, with no tombstone kept, because from this client's point of view the row
    //    was gone.
    //
    //    Bounded: see REGISTRATION_SETTLE_TIMEOUT_MS for why, and for what is done
    //    about the case where the bound expires.
    let overtakenByRegistration = false;

    if (state.pending_registration_request) {
      const settled = await settleWithin(
        state.pending_registration_request,
        REGISTRATION_SETTLE_TIMEOUT_MS,
      );

      overtakenByRegistration = !settled;

      commit('set_state', { property: 'pending_registration_request', value: null });
    }

    // 3. AND ONLY NOW READ THE TOKEN. After the wait, not before it: if the
    //    registration that was in flight was for a token this device had only just
    //    been handed, that is the token the server now holds, and it is the one the
    //    DELETE has to name.
    const { token } = state;

    commit('set_state', { property: 'registered', value: false });
    commit('set_state', { property: 'token', value: null });
    // Cleared together with the claim, for the same reason they are written
    // together. Leaving the recorded token behind would mean that a member who
    // logged out and back in on the same handset -- same platform token, because
    // the OS did not rotate it -- would have their next registration compared
    // against a claim that the DELETE below has already made false, and the POST
    // that recreates the row would be skipped as redundant. The row would stay
    // deleted and the device would be silently unreachable for the whole session.
    commit('set_state', { property: 'registered_token', value: null });
    // PER-PROCESS. stopRegistration() has just removed every native listener, so
    // this process is no longer ignited and must stop claiming that it is.
    commit('set_state', { property: 'session_registered', value: false });

    if (!token) {
      return false;
    }

    return dispatch(
      'api/deleteEndpoint',
      {
        endpoint: '/push-tokens',
        data: { token },
      },
      { root: true },
    )
      .then(() => {
        // Clear the tombstone only if it named THIS token AND THIS MEMBER. A DELETE
        // that succeeded for token B is not evidence about an outstanding token A,
        // and clearing unconditionally would silently drop the one record that says
        // the server still holds a row nobody is going to remove. There is a test
        // with two different tokens.
        //
        // THE OWNER HALF IS FINDING F2 ON THE LOGOUT PATH, and it is not the same
        // case as the token half. The platform token belongs to the INSTALLATION, so
        // after a handover member B is holding the very token A's tombstone names --
        // and B's 204 for it says only that B has no such row, never that A's was
        // removed. Matching on the token alone would let B's own logout spend A's
        // tombstone, which is the finding arriving by a second route.
        // UNLESS THE WAIT ABOVE TIMED OUT. Then this DELETE may yet be overtaken by
        // the POST it did not manage to wait for, so the revocation is recorded as
        // still outstanding rather than assumed done -- under THIS member, so only
        // this member can spend it. The next login re-issues it, which costs one
        // redundant DELETE in the rare case the POST had in fact already failed.
        if (overtakenByRegistration) {
          commit('set_state', {
            property: 'pending_unregistration',
            value: { token, owner_user_id: owner },
          });

          return false;
        }

        const outstanding = getters.pending_unregistration;

        if (outstanding
          && outstanding.token === token
          && isSameMember(outstanding.owner_user_id, owner)) {
          clearPendingUnregistration(commit);
        }

        return true;
      })
      .catch(() => {
        // Logout must never fail because unregistration failed -- so this is
        // still swallowed, and the member is still logged out.
        //
        // WHAT CHANGED (C1): it is recorded instead of discarded. The comment
        // that used to sit here said "worst case the server keeps a token that
        // its own reaper will remove on first failure", and that reasoning is
        // wrong in the one case that matters. The reaper reacts to DELIVERY
        // FAILURES. A handset that has just been handed to somebody else is
        // reachable, so every send SUCCEEDS, and the reaper never hears anything
        // at all. Nothing was ever going to remove this row.
        //
        // The token is stored rather than a flag because `token` above has
        // already been cleared by the time anything can act on this -- and the OWNER
        // is stored beside it (finding F2), because on the next login the only
        // question worth asking is whether the member standing there is the one who
        // owes this DELETE.
        commit('set_state', {
          property: 'pending_unregistration',
          value: { token, owner_user_id: owner },
        });

        return false;
      });
  },

  /**
   * Pay off a DELETE that a previous logout could not deliver (finding C1).
   *
   * Dispatched from the ignition (src/js/push-session.js) on every launch that
   * has an authenticated, non-impersonated session -- which is the first moment
   * the retry is possible, because DELETE /push-tokens is an authenticated call.
   *
   * IT MUST RUN BEFORE `register`, and this is not a preference. The OS does not
   * rotate a token merely because somebody logged out, so the handset usually
   * comes back holding exactly the token the tombstone names. Draining afterwards
   * would delete the row the same launch had just created and leave the member
   * with no notifications for the entire session -- a worse bug than the one this
   * fixes. The ignition awaits it for that reason.
   *
   * NON-BLOCKING AND SILENT. It never rejects and it raises no toast: it runs on
   * behalf of a session that has already ended, on a login the member started for
   * some other purpose, and a red bar about a device registry they have never
   * heard of is noise they cannot act on. A failure simply leaves the tombstone
   * for the next launch.
   *
   * WHY A FAILED RETRY IS THE ONLY THING THAT KEEPS IT. DELETE /push-tokens
   * answers 204 whether or not it deleted anything
   * (PushTokenController::destroy() -- "a logout must not fail because the token
   * was already gone"), so a resolved promise means "the server has nothing
   * outstanding for me", never "a row was definitely removed". That is exactly
   * the right condition to clear on: keeping the tombstone after a 204 would
   * re-issue the same DELETE on every login for the rest of the installation's
   * life.
   *
   * IT DRAINS ONLY FOR THE MEMBER WHO CREATED IT (finding F2), and the reason is read
   * off that same controller rather than assumed: the delete is scoped to
   * `$request->user()->pushTokens()`. So a DELETE sent by anybody else cannot remove
   * the row -- and, because `destroy()` answers 204 either way, it comes back looking
   * exactly like success. Plan 39.5-05 cleared the tombstone on that 204. The result
   * was that a handset handed from A to B lost A's outstanding revocation on B's
   * first login, while A's registration stayed live and kept delivering A's private
   * notifications to B's phone, with plan 39.5-04's 409 preventing B from claiming
   * the device and correcting it.
   *
   * SO A MISMATCH RETRIES THE OTHER HALF INSTEAD. `revokeOsRegistration()` talks to
   * Apple/Google about the DEVICE, needs no server authority, and can therefore be
   * performed by whoever is holding it. Once it succeeds the old token is
   * undeliverable, the server's next send to it fails, and the reaper removes the
   * row -- which is the entire chain that protects A. If it fails, the tombstone
   * survives for the next launch, because a revocation that failed twice is still
   * owed.
   *
   * AND IT RUNS BEFORE `push/register`, which the ignition already guarantees for the
   * ordering reason above and which matters twice as much now: revoking the OS
   * registration AFTER this launch had registered would tear down the route the
   * CURRENT member just established for themselves.
   *
   * AN UNKNOWN OWNER IS TREATED AS SOMEBODY ELSE'S. See `isSameMember` and the legacy
   * field in `state()`: that is the migration decision for a tombstone persisted
   * under plan 39.5-05's shape.
   *
   * WHAT IT STILL CANNOT DO. The previous member's server row is not deleted by this
   * client -- it cannot be, without a global-by-token DELETE that would reopen
   * findings C2 and C4. It is made UNDELIVERABLE, and the server's reaper is what
   * finally removes it.
   *
   * @returns {Promise<boolean>} true only when the outstanding revocation was
   *   discharged -- either the DELETE was accepted, or the OS registration was really
   *   revoked.
   */
  async retry_pending_unregistration({ state, commit, dispatch, getters, rootGetters }) {
    const outstanding = getters.pending_unregistration;

    // By far the common case: this runs on every login.
    if (!outstanding) {
      return false;
    }

    const { token } = outstanding;

    if (!isSameMember(outstanding.owner_user_id, rootGetters['current_user/id'])) {
      // NOT OURS TO SEND. No DELETE is issued at all -- not because it would be
      // refused, but because it would SUCCEED, meaninglessly, and its 204 is what
      // used to clear this tombstone forever.
      const revoked = await revokeOsRegistration();

      if (revoked) {
        // Discharged: the OS route is gone, so the row is undeliverable and the
        // reaper will take it. Keeping the tombstone here instead would re-run this
        // revocation on every login for the rest of the installation's life -- and on
        // Android `unregister()` deletes the FCM token, so that would rotate the
        // CURRENT member's token on every launch.
        clearPendingUnregistration(commit);

        return true;
      }

      // Still owed. Nothing is written: the record already says everything the next
      // launch needs.
      return false;
    }

    return dispatch(
      'api/deleteEndpoint',
      {
        endpoint: '/push-tokens',
        data: { token },
        suppress_error_toast: true,
      },
      { root: true },
    )
      .then(() => {
        clearPendingUnregistration(commit);

        return true;
      })
      .catch(() => {
        // DISCARDED ON PURPOSE, and this is the one place in this module where
        // that is the right answer -- so it is written out rather than left as a
        // bare `() => false`, which is the shape finding C1 was hiding in.
        //
        // The difference is that nothing is lost here. `unregister`'s catch had
        // to RECORD the failure because the token was about to be cleared and
        // there would have been nothing left to retry from. Here the tombstone is
        // already the record, and leaving it untouched IS the handling: the next
        // launch runs this again. Re-writing it would be a no-op, and rethrowing
        // would break the login this sits on for a device registry the member
        // never asked about.
        return false;
      });
  },

  /**
   * PUSH-07: route a tapped notification to the right screen.
   *
   * The payload's `screen` is looked up in an allowlist; anything unrecognised
   * lands on home rather than being followed, so a malformed payload cannot
   * navigate the WebView somewhere arbitrary.
   */
  handle_notification_tap({ commit }, data) {
    commit('set_state', { property: 'pending_navigation', value: data || {} });

    return data;
  },

  /**
   * Hand the app shell the route a tapped notification should open, exactly once.
   *
   * Held in the store rather than acted on directly because a cold-start tap can
   * fire before the router exists -- Capacitor queues the event until a listener
   * is attached, so the tap that LAUNCHED the app is delivered during boot, well
   * before there is anything to navigate. The consumer is app.vue; see Plan
   * 39-10. Until that lands, `pending_navigation` is written and never read,
   * which is the state Phase 38 left PUSH-07's deep-link half in.
   *
   * CONSUMED EXACTLY ONCE. Clearing on read is what stops the same notification
   * being navigated a second time the next time anything touches the store --
   * which, with a reactive consumer watching this key, would otherwise yank the
   * member off whatever screen they had since walked to.
   *
   * The path always comes from the SCREEN_ROUTES allowlist via resolveScreenRoute
   * and NEVER from the payload directly. The payload arrives from outside the app
   * and the WebView it would be steering holds a bearer token that opens the
   * gym's door, so the allowlist is a real control rather than hygiene: an
   * unknown or hostile `screen` resolves to home instead of being followed.
   *
   * EVERY VALUE IN THE ALLOWLIST IS A COMPLETE PATH, so this returns one
   * unchanged. 40-05 had `betalingen` as `/users/:user_id` and substituted the
   * session's member id here, at consume time; 40-09 repointed the key at
   * `/account/abonnement` and retired the substitution with it. Nothing about
   * a destination now depends on who is logged in, which also means a tap left
   * pending across a logout on a shared handset cannot resolve into anyone's
   * account record (T-40-22) -- there is no record in the map to resolve into.
   *
   * The page on the other end is what keeps that safe: /account/abonnement
   * reads the member id from the session and fetches nothing at all when there
   * is none.
   *
   * @returns {string|null} a path from the SCREEN_ROUTES allowlist, or null if
   *   nothing is pending
   */
  consume_pending_navigation({ state, commit }) {
    const pending = state.pending_navigation;

    if (!pending) {
      return null;
    }

    const path = resolveScreenRoute(pending.screen);

    // Phase 41. The tapped payload's target id is preserved for the landing screen
    // -- and ONLY for the landing screen. It is scroll-into-view and `source`
    // EVIDENCE; it never steers the path (that is resolveScreenRoute's job, from
    // the allowlist) and it never authorises anything.
    //
    // COORDINATION, and read this before editing. Phase 40's plan 40-05 edits this
    // same action, and it LANDED FIRST (2026-08-20): it put the SESSION's own id
    // into the PATH for `/users/:user_id`, read from `rootState` and never from the
    // payload, and shipped a test -- "a tapped payment notification ignores an id
    // in the payload" -- plus mutation M11, which reds if this action reads
    // `pending.id` to build the PATH. 40-09 then repointed `betalingen` at
    // `/account/abonnement` and retired the substitution, which is why no
    // `resolveSelfPath` call is above this line any more.
    //
    // This commit is compatible with all of that, and 41-06 re-read the action as
    // it actually stood rather than applying its planned diff blind: `pending.id`
    // is read only as DATA, into a separate state key, and `path` above is
    // untouched. 41-06 ships its own test asserting an unknown screen with an id
    // still resolves to home. Anything that edits this action next owes the same
    // re-read.
    commit('set_state', {
      property: 'last_tapped_target',
      value: { screen: pending.screen, id: pending.id ?? null },
    });

    commit('set_state', { property: 'pending_navigation', value: null });

    return path;
  },

  /**
   * Read the tapped target ONCE. UI-SPEC IC5: the binding is spent by the first
   * confirm of that id, and does not survive the page unmount -- so a member who
   * lands from a push, walks away and comes back later is confirming from inside
   * the app, and a confirm of a DIFFERENT booking during the same visit is
   * `source: 'app'` too.
   *
   * @returns {{screen: string, id: number|string|null}|null} the parked target the
   *   first time it is read, null every time after
   */
  consume_last_tapped_target({ state, commit }) {
    const target = state.last_tapped_target;

    if (!target) {
      return null;
    }

    commit('set_state', { property: 'last_tapped_target', value: null });

    return target;
  },

  /**
   * PREF-05: record that this device has now seen the explainer.
   *
   * Called whether the member accepted or declined it. Declining is an answer,
   * and re-asking on the next booking is the nagging the flag exists to prevent;
   * the settings screen keeps offering the prompt for anyone who changes their
   * mind.
   */
  mark_permission_prompt_shown({ commit }) {
    commit('set_state', { property: 'permission_prompt_shown', value: true });

    return true;
  },
};

const mutations = {
  ...BaseModule.mutations,

  /**
   * Forget everything that only meant something inside the PREVIOUS process.
   *
   * Dispatched once at boot, from src/store/index.js, immediately after
   * vuex-persist has rehydrated the store and before anything can read it. That
   * placement is the whole point: the plugin does its `replaceState` synchronously
   * while it is being installed, so committing this straight afterwards means no
   * consumer ever sees a restored per-process value.
   *
   * WHY THIS AND NOT A vuex-persist REDUCER. See the module docblock: a reducer
   * applies to the entire store and the first module added after it would silently
   * stop persisting. This is local to this module and cannot affect another one.
   *
   * WHAT IT MUST NEVER TOUCH -- and it is exactly three fields, each for its own
   * reason:
   *   - `token`                    logout's DELETE /push-tokens needs it, and
   *                                without it a logged-out handset keeps receiving
   *                                the member's notifications.
   *   - `permission_prompt_shown`  PREF-05. iOS's permission prompt is ONE-SHOT and
   *                                this flag is what protects it. Clearing it here
   *                                would re-show the explainer on every single
   *                                launch, to every member, forever -- which is the
   *                                nagging it exists to prevent. There is a test.
   *   - `pending_unregistration` and its LEGACY sibling
   *     `pending_unregistration_token`
   *                                Finding C1, as finding F2 corrected it. It records
   *                                a DELETE this device still owes the server AND the
   *                                member who owes it, and its whole purpose is to
   *                                survive the process that failed to make it.
   *                                Clearing it here would drop the outstanding work
   *                                on the very next launch -- which is the launch it
   *                                was written for. Sweeping it in beside
   *                                `registered` is the obvious mistake for a reader
   *                                who learned this module's per-process rule and
   *                                stopped there; see the module docblock for why
   *                                the two are not the same kind of thing. Both
   *                                fields have a test.
   *
   * `registered` and `registered_token` ARE cleared, and that is deliberate rather
   * than incidental -- see their annotations in `state()`. A claim the client cannot
   * verify must not outlive the process that made it: the server's reaper can delete
   * a live token's row without telling the handset, and a surviving claim would skip
   * the very POST that recreates it. One request per launch is the price of that
   * being self-healing.
   *
   * `pending_registration_request` IS cleared, and it could not be anything else: a
   * request made by a process that no longer exists is not in flight, and what
   * survives the restart is a `{}` -- vuex-persist's rendering of a Promise -- which
   * is truthy and would make `unregister` wait on an object that will never settle,
   * for the whole of REGISTRATION_SETTLE_TIMEOUT_MS, on the first logout after every
   * launch.
   *
   * `pending_navigation` IS cleared, and that is a fix rather than tidying: a tap
   * recorded moments before the app was killed used to survive to the next launch,
   * where app.vue's f7Ready watcher consumed it and yanked the member onto a screen
   * they never asked for. A genuine cold-start tap cannot be lost to this, because
   * Capacitor only delivers the queued event once a listener exists, and listeners
   * are attached from register() -- long after this runs.
   *
   * `last_tapped_target` IS cleared, for the same reason and one more of its own:
   * it is per-process, and the store's vuex-persist has DELIBERATELY no reducer
   * (src/store/index.js), so anything left in state is written to localStorage and
   * read back on the next launch -- where it would describe a tap from a process
   * that no longer exists, to whoever is holding the handset now.
   */
  reset_session_state(state) {
    state.session_registered = false;
    state.pending_registration_request = null;
    state.registered = false;
    state.registered_token = null;
    state.registration_error = null;
    state.pending_navigation = null;
    state.last_tapped_target = null;
    state.permission = null;
  },
};

export default {
  namespaced: true,

  state,
  getters,
  actions,
  mutations,
};
